import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Campaigns
export const createCampaign = (data) => api.post('/api/campaigns', data);
export const getCampaigns = (status) => api.get('/api/campaigns', { params: { status } });
export const getCampaign = (id) => api.get(`/api/campaigns/${id}`);
export const updateCampaign = (id, data) => api.put(`/api/campaigns/${id}`, data);
export const deleteCampaign = (id) => api.delete(`/api/campaigns/${id}`);
export const approveCampaign = (id) => api.post(`/api/campaigns/${id}/approve`);
export const rerunStep = (id, step) => api.post(`/api/campaigns/${id}/rerun/${step}`);
export const reviseCampaign = (id, data) => api.post(`/api/campaigns/${id}/revise`, data);
export const continueCampaign = (id, approvedMd) => api.post(`/api/campaigns/${id}/continue`, { approved_md: approvedMd });
export const sendTestEmail = (id, data) => api.post(`/api/campaigns/${id}/send-test`, data);
export const sendCampaign = (id, data) => api.post(`/api/campaigns/${id}/send`, data);
export const getMaterialTypes = () => api.get('/api/material-types');

// Contact Lists
export const createList = (data) => api.post('/api/lists', data);
export const getLists = () => api.get('/api/lists');
export const getList = (id) => api.get(`/api/lists/${id}`);
export const deleteList = (id) => api.delete(`/api/lists/${id}`);

// Contacts
export const createContact = (data) => api.post('/api/contacts', data);
export const getContacts = (listId) => api.get('/api/contacts', { params: { list_id: listId } });
export const updateContact = (id, data) => api.put(`/api/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/api/contacts/${id}`);
export const addContactTags = (id, tags) => api.post(`/api/contacts/${id}/tags`, { tags });
export const bulkTagList = (listId, tags) => api.post(`/api/lists/${listId}/bulk-tag`, { tags });
export const removeContactTag = (id, tag) => api.delete(`/api/contacts/${id}/tags/${tag}`);
export const getAllTags = () => api.get('/api/contacts/tags/all');
export const previewSegment = (listId, filters) => api.post(`/api/lists/${listId}/segment`, filters);
export const importCSV = (file, listId) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('list_id', listId);
  return api.post('/api/contacts/import-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: { list_id: listId },
  });
};

// SMTP Settings
export const getSMTPSettings = () => api.get('/api/settings/smtp');
export const saveSMTPSettings = (data) => api.post('/api/settings/smtp', data);
export const testSMTP = (data) => api.post('/api/settings/smtp/test', data);

// Forms
export const createForm = (data) => api.post('/api/forms', data);
export const getForms = () => api.get('/api/forms');
export const getForm = (id) => api.get(`/api/forms/${id}`);
export const updateForm = (id, data) => api.put(`/api/forms/${id}`, data);
export const deleteForm = (id) => api.delete(`/api/forms/${id}`);

// Form Submissions
export const getFormSubmissions = (formId) => api.get('/api/form-submissions', { params: { form_id: formId } });

// Leads
export const getLeads = () => api.get('/api/leads');
export const deleteLead = (id) => api.delete(`/api/leads/${id}`);

// Analytics
export const getAnalyticsOverview = () => api.get('/api/analytics/overview');
export const getCampaignAnalytics = (id) => api.get(`/api/analytics/campaign/${id}`);
export const getRecentEvents = (limit) => api.get('/api/analytics/recent-events', { params: { limit } });

// A/B Testing
export const createABTest = (campaignId, data) => api.post(`/api/campaigns/${campaignId}/ab-test`, data);
export const getABResults = (campaignId) => api.get(`/api/campaigns/${campaignId}/ab-results`);

// Scheduling
export const scheduleCampaign = (campaignId, data) => api.post(`/api/campaigns/${campaignId}/schedule`, data);
export const cancelSchedule = (campaignId) => api.post(`/api/campaigns/${campaignId}/cancel-schedule`);
export const getScheduledCampaigns = () => api.get('/api/campaigns/scheduled/list');

// Exports
export const exportCampaigns = (format) => api.get('/api/exports/campaigns', { params: { format }, responseType: format === 'json' ? 'json' : 'blob' });
export const exportLeads = (format) => api.get('/api/exports/leads', { params: { format }, responseType: format === 'json' ? 'json' : 'blob' });
export const exportContacts = (listId, format) => api.get('/api/exports/contacts', { params: { list_id: listId, format }, responseType: format === 'json' ? 'json' : 'blob' });
export const exportEvents = (campaignId, format) => api.get('/api/exports/events', { params: { campaign_id: campaignId, format }, responseType: format === 'json' ? 'json' : 'blob' });

// Jobs
export const getJobs = (status) => api.get('/api/jobs', { params: { status } });
export const getJob = (id) => api.get(`/api/jobs/${id}`);

// Auth
export const registerUser = (data) => api.post('/api/auth/register', data);
export const loginUser = (data) => api.post('/api/auth/login', data);
export const getCurrentUser = () => api.get('/api/auth/me');
export const listUsers = () => api.get('/api/auth/users');
export const updateUser = (id, data) => api.put(`/api/auth/users/${id}`, data);
export const deactivateUser = (id) => api.post(`/api/auth/users/${id}/deactivate`);

// Set auth token
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('cc_token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('cc_token');
  }
};

// Init token from storage
const savedToken = localStorage.getItem('cc_token');
if (savedToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Suppression List
export const getSuppressionList = () => api.get('/api/suppression-list');
export const addToSuppression = (data) => api.post('/api/suppression-list', data);
export const removeFromSuppression = (email) => api.delete(`/api/suppression-list/${email}`);
export const importSuppression = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/suppression-list/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Deliverability
export const getDNSRecords = () => api.get('/api/deliverability/dns-records');
export const saveDKIMConfig = (data) => api.post('/api/deliverability/dkim', data);
export const getRateLimit = () => api.get('/api/deliverability/rate-limit');
export const getDeliverabilityHealth = () => api.get('/api/deliverability/health');

// Audit
export const getAuditLog = (limit) => api.get('/api/audit-log', { params: { limit } });

// Chat
export const sendChatMessage = (message, sessionId, campaignId) => api.post('/api/chat', { message, session_id: sessionId, campaign_id: campaignId });
export const getChatHistory = (sessionId) => api.get(`/api/chat/history/${sessionId}`);
export const getChatSessions = () => api.get('/api/chat/sessions');
export const deleteChatSession = (sessionId) => api.delete(`/api/chat/sessions/${sessionId}`);

// Agent Configs
export const createAgentConfig = (data) => api.post('/api/agent-configs', data);
export const getAgentConfigs = () => api.get('/api/agent-configs');
export const getAgentConfig = (id) => api.get(`/api/agent-configs/${id}`);
export const updateAgentConfig = (id, data) => api.put(`/api/agent-configs/${id}`, data);
export const deleteAgentConfig = (id) => api.delete(`/api/agent-configs/${id}`);
export const mapAgentToStep = (agentId, stepKey) => api.post(`/api/agent-configs/${agentId}/map-step/${stepKey}`);

// Skills
export const createSkill = (data) => api.post('/api/skills', data);
export const getSkills = () => api.get('/api/skills');
export const getSkill = (id) => api.get(`/api/skills/${id}`);
export const updateSkill = (id, data) => api.put(`/api/skills/${id}`, data);
export const deleteSkill = (id) => api.delete(`/api/skills/${id}`);

// Workflows
export const createWorkflow = (data) => api.post('/api/workflows', data);
export const getWorkflows = () => api.get('/api/workflows');
export const getWorkflow = (id) => api.get(`/api/workflows/${id}`);
export const updateWorkflow = (id, data) => api.put(`/api/workflows/${id}`, data);
export const deleteWorkflow = (id) => api.delete(`/api/workflows/${id}`);

// Branding
export const getBranding = () => api.get('/api/settings/branding');
export const saveBranding = (data) => api.post('/api/settings/branding', data);
export const uploadLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/settings/branding/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// AI Form Generation
export const aiGenerateForm = (data) => api.post('/api/forms/ai-generate', data);

// Tools
export const getTools = () => api.get('/api/tools');
export const executeTool = (toolId, params) => api.post(`/api/tools/${toolId}/execute`, { params });
export const createTool = (data) => api.post('/api/tools', data);
export const getAgentTools = (agentId) => api.get(`/api/agents/${agentId}/tools`);

// LLM Providers (Custom)
export const createLLMProvider = (data) => api.post('/api/llm-providers', data);
export const updateLLMProvider = (id, data) => api.put(`/api/llm-providers/${id}`, data);
export const deleteLLMProvider = (id) => api.delete(`/api/llm-providers/${id}`);
export const discoverModels = (data) => api.post('/api/llm-providers/discover-models', data);
export const testLLMProvider = (data) => api.post('/api/llm-providers/test', data);

// Commercial API Keys (Direct)
export const getCommercialKeys = () => api.get('/api/settings/api-keys');
export const saveCommercialKey = (data) => api.post('/api/settings/api-keys', data);
export const deleteCommercialKey = (provider) => api.delete(`/api/settings/api-keys/${provider}`);
export const testCommercialKey = (data) => api.post('/api/settings/api-keys/test', data);

// Seed Defaults
export const seedDefaults = () => api.post('/api/seed-defaults');

// @Mention Agents
export const getAgentsForMentions = () => api.get('/api/agents/mentions');

// CXO Review
export const getCXOPersonas = () => api.get('/api/cxo/personas');
export const triggerCXOReview = (campaignId, data) => api.post(`/api/cxo/review/${campaignId}`, data);
export const getCXOReview = (campaignId) => api.get(`/api/cxo/review/${campaignId}`);

// LLM Providers
export const getLLMProviders = () => api.get('/api/llm-providers');

// MCP Configuration
export const getAgentMCP = (agentId) => api.get(`/api/agent-configs/${agentId}/mcp`);
export const updateAgentMCP = (agentId, data) => api.put(`/api/agent-configs/${agentId}/mcp`, data);

// Brand Themes
export const getBrandThemes = () => api.get('/api/branding/themes');
export const createBrandTheme = (data) => api.post('/api/branding/themes', data);
export const updateBrandTheme = (id, data) => api.put(`/api/branding/themes/${id}`, data);
export const activateBrandTheme = (id) => api.post(`/api/branding/themes/${id}/activate`);

// Bulk Campaign Operations
export const bulkDeleteCampaigns = (campaignIds) => api.post('/api/campaigns/bulk-delete', { campaign_ids: campaignIds });
export const bulkRetryCampaigns = (campaignIds) => api.post('/api/campaigns/bulk-retry', { campaign_ids: campaignIds });
export const retryCampaign = (id) => api.post(`/api/campaigns/${id}/retry`);

// Content Library
export const getContentLibrary = (category) => api.get('/api/content-library', { params: category ? { category } : {} });
export const getContentAsset = (id) => api.get(`/api/content-library/${id}`);
export const uploadContentFile = (file, metadata = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (metadata.title) formData.append('title', metadata.title);
  if (metadata.description) formData.append('description', metadata.description);
  if (metadata.category) formData.append('category', metadata.category);
  if (metadata.tags) formData.append('tags', metadata.tags);
  return api.post('/api/content-library/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const updateContentAsset = (id, data) => api.put(`/api/content-library/${id}`, data);
export const deleteContentAsset = (id) => api.delete(`/api/content-library/${id}`);

// Knowledge Base
export const getKnowledgeAssets = () => api.get('/api/knowledge-base');
export const getKnowledgeAsset = (id) => api.get(`/api/knowledge-base/${id}`);
export const updateKnowledgeAsset = (id, data) => api.put(`/api/knowledge-base/${id}`, data);
export const deleteKnowledgeAsset = (id) => api.delete(`/api/knowledge-base/${id}`);

// Chat File Upload
export const uploadChatFile = (file, sessionId, campaignId = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('session_id', sessionId);
  formData.append('campaign_id', campaignId);
  return api.post('/api/chat/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: { session_id: sessionId, campaign_id: campaignId },
  });
};

export default api;
